import { forwardRef, ComponentProps, MouseEvent as ReactMouseEvent, useCallback, PropsWithChildren } from 'react';
import NextLink from 'next/link';
import { useNavigationContext, cn } from '@restworld/utils-common';

import { IconComponentType } from '../icon-button';

const LINK_DELAY = 200;

const DEFAULT_HREF = '#!';

export const Link = forwardRef<HTMLAnchorElement, LinkProps>((props, ref) => {
  const {
    disabled,
    onClick,
    children,
    twStyle,
    StartIcon,
    EndIcon,
    newTab = true,
    href = DEFAULT_HREF,
    size = 'md',
    variant = 'neutral',
    iconStyle,
    className,
    ...restProps
  } = props;

  const { router } = useNavigationContext();
  const iconClasses = iconStyle || 'h-4 w-4';

  const handleClick = useCallback(
    (e: ReactMouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      if (disabled) return;

      onClick && onClick(e);

      setTimeout(() => {
        if (newTab) {
          window.open(href, '_blank', 'noopener noreferrer');
        } else {
          href !== DEFAULT_HREF && router.push(href);
        }
      }, LINK_DELAY);
    },
    [disabled, href, newTab, onClick, router]
  );

  return (
    // Modern next/link renders its own <a> — no more `legacyBehavior`/`passHref` + nested anchor
    // (the old `as="div"` was inert since handleClick preventDefaults and navigates manually).
    <NextLink
      ref={ref}
      href={href}
      className={cn(
        'flex items-center gap-2 no-underline text-inherit rounded-lg w-fit font-medium transition duration-200 ease-in-out hover:cursor-pointer focus-visible:outline-[8px] focus-visible:outline-red-200',
        size === 'sm' ? 'p-1 text-sm h-8' : 'py-1 px-2 text-base h-10',
        variantClasses[variant],
        disabled && 'text-neutral-100 hover:bg-transparent hover:cursor-not-allowed',
        twStyle,
        className
      )}
      onClick={(e) => handleClick(e)}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      {...restProps}
    >
      {StartIcon && <StartIcon className={iconClasses} />}
      {children}
      {EndIcon && <EndIcon className={iconClasses} />}
    </NextLink>
  );
});
Link.displayName = 'Link';

export default Link;

export interface LinkProps extends ComponentProps<'a'>, PropsWithChildren<StyledLinkProps> {
  StartIcon?: IconComponentType;
  EndIcon?: IconComponentType;
  disabled?: boolean;
  twStyle?: string;
  iconStyle?: string;
  newTab?: boolean;
}
type LinkSizeType = 'sm' | 'md';
type LinkVariantType = 'neutral' | 'success' | 'warning' | 'error' | 'restworld' | 'reversed';
interface StyledLinkProps {
  size?: LinkSizeType;
  variant?: LinkVariantType;
  disabled?: boolean;
  twStyle?: string;
}

const variantClasses: Record<LinkVariantType, string> = {
  success: 'text-green-700 hover:bg-green-700/10 active:bg-green-700/20',
  warning: 'text-orange-700 hover:bg-orange-700/10 active:bg-orange-700/20',
  error: 'text-red-700 hover:bg-red-700/10 active:bg-red-700/20',
  restworld: 'text-purple-700 hover:bg-purple-700/10 active:bg-purple-700/20',
  reversed: 'text-white hover:bg-white/10',
  neutral: 'text-neutral-700 hover:bg-neutral-900/10 active:bg-neutral-900/20',
};
